import React from 'react';
import {
    Alert, Box, Button, Card, Chip, CircularProgress, FormControl, InputLabel, MenuItem, Select, Typography,
} from '@mui/material';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckIcon from '@mui/icons-material/Check';
import { Translate } from 'react-localize-redux';
import type { OpenCorrection } from '../../../services/qualityCheck';
import CategoryFeedback from '../QualityCheck/CategoryFeedback';
import { CorrectionFlagPerson, latestFlaggingEvent, personName } from '../QualityCheck/correctionFlag';
import { QcCategorySection } from '../QualityCheck/qcCategories';

const T = 'hospital.my-corrections';

export type CorrectionsState =
    | { kind: 'loading' }
    | { kind: 'error'; message: string | null }
    | { kind: 'ready'; corrections: OpenCorrection[]; reasonSections: QcCategorySection[] };

export interface CorrectionsPanelProps {
    state: CorrectionsState;
    /** SUPER_ADMIN: the whole study, each row's assignee, and a filter by assignee. */
    studyWide: boolean;
    /** The active language code, for the dates. */
    localeCode: string;
    /** The assignee shown in the study-wide list; null shows every one. */
    assigneeFilter: string | null;
    onAssigneeFilterChange: (assigneeUuid: string | null) => void;
    onOpenPatientRecord: (correction: OpenCorrection) => void;
    onMarkCorrected: (correction: OpenCorrection) => void;
    onRetry: () => void;
}

const ALL_ASSIGNEES = '';
const NO_OP = () => undefined;

/** The first block of the visit uuid: the list serves no visit date or patient label (SKIN-599). */
export const shortVisitId = (visitUuid: string) => visitUuid.split('-')[0];

/**
 * SKIN-600 — the open correction flags (SKIN-577) of the caller, or of the whole study for a
 * SUPER_ADMIN. Presentational: the container reads the list and owns the confirmation.
 *
 * Two groups, by the flag's state: OPEN is still to correct; CORRECTED was marked corrected and
 * waits for the reviewer — still open, so still listed (and still correctable), but no longer
 * "to correct".
 */
export default function CorrectionsPanel({
    state, studyWide, localeCode, assigneeFilter, onAssigneeFilterChange, onOpenPatientRecord, onMarkCorrected, onRetry,
}: CorrectionsPanelProps) {
    return (
        <Box data-testid="corrections-panel">
            <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>
                <Translate id={studyWide ? `${T}.title-study` : `${T}.title`} />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                <Translate id={studyWide ? `${T}.intro-study` : `${T}.intro`} />
            </Typography>
            <PanelBody state={state} studyWide={studyWide} localeCode={localeCode} assigneeFilter={assigneeFilter}
                onAssigneeFilterChange={onAssigneeFilterChange} onOpenPatientRecord={onOpenPatientRecord}
                onMarkCorrected={onMarkCorrected} onRetry={onRetry} />
        </Box>
    );
}

function PanelBody({ state, studyWide, localeCode, assigneeFilter, onAssigneeFilterChange, onOpenPatientRecord, onMarkCorrected, onRetry }: CorrectionsPanelProps) {
    if (state.kind === 'loading') {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
    }
    if (state.kind === 'error') {
        return (
            <Alert severity="error" data-testid="corrections-failed"
                action={<Button color="inherit" size="small" onClick={onRetry} data-testid="corrections-retry"><Translate id={`${T}.retry`} /></Button>}>
                <Translate id={`${T}.failed`} />
                {state.message && <Typography variant="caption" display="block">{state.message}</Typography>}
            </Alert>
        );
    }
    const shown = studyWide && assigneeFilter
        ? state.corrections.filter((correction) => correction.flag.current?.assignee?.uuid === assigneeFilter)
        : state.corrections;
    const toCorrect = shown.filter((correction) => correction.flag.current?.state === 'OPEN');
    const awaitingReview = shown.filter((correction) => correction.flag.current?.state === 'CORRECTED');
    const row = (correction: OpenCorrection) => (
        <CorrectionRow key={correction.visitUuid} correction={correction} reasonSections={state.reasonSections}
            studyWide={studyWide} localeCode={localeCode} onOpenPatientRecord={onOpenPatientRecord} onMarkCorrected={onMarkCorrected} />
    );

    return (
        <React.Fragment>
            {studyWide && (
                <AssigneeFilter corrections={state.corrections} value={assigneeFilter} onChange={onAssigneeFilterChange} />
            )}
            {shown.length === 0 ? (
                <Alert severity="success" data-testid="corrections-empty"><Translate id={`${T}.empty`} /></Alert>
            ) : (
                <React.Fragment>
                    <Group testId="corrections-to-correct" titleId={`${T}.group.to-correct`} count={toCorrect.length}>
                        {toCorrect.map(row)}
                    </Group>
                    <Group testId="corrections-awaiting-review" titleId={`${T}.group.awaiting-review`} count={awaitingReview.length}>
                        {awaitingReview.map(row)}
                    </Group>
                </React.Fragment>
            )}
        </React.Fragment>
    );
}

function Group({ testId, titleId, count, children }: { testId: string; titleId: string; count: number; children: React.ReactNode }) {
    if (count === 0) return null;
    return (
        <Box component="section" data-testid={testId} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
                <Translate id={titleId} />{` (${count})`}
            </Typography>
            {children}
        </Box>
    );
}

/** Every assignee the study's list names, once, in the order met. */
function assigneesOf(corrections: OpenCorrection[]): CorrectionFlagPerson[] {
    const byUuid = new Map<string, CorrectionFlagPerson>();
    corrections.forEach((correction) => {
        const assignee = correction.flag.current?.assignee;
        if (assignee && !byUuid.has(assignee.uuid)) byUuid.set(assignee.uuid, assignee);
    });
    return Array.from(byUuid.values());
}

function AssigneeFilter({ corrections, value, onChange }: { corrections: OpenCorrection[]; value: string | null; onChange: (uuid: string | null) => void }) {
    return (
        <FormControl size="small" sx={{ minWidth: 260, mb: 2 }} data-testid="assignee-filter">
            <InputLabel id="assignee-filter-label"><Translate id={`${T}.assignee-filter`} /></InputLabel>
            <Select
                labelId="assignee-filter-label"
                label={<Translate id={`${T}.assignee-filter`} />}
                value={value ?? ALL_ASSIGNEES}
                onChange={(event) => onChange(event.target.value === ALL_ASSIGNEES ? null : String(event.target.value))}
            >
                <MenuItem value={ALL_ASSIGNEES}><Translate id={`${T}.all-assignees`} /></MenuItem>
                {assigneesOf(corrections).map((person) => (
                    <MenuItem key={person.uuid} value={person.uuid}>{personName(person) ?? person.uuid}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}

function CorrectionRow({ correction, reasonSections, studyWide, localeCode, onOpenPatientRecord, onMarkCorrected }: {
    correction: OpenCorrection;
    reasonSections: QcCategorySection[];
    studyWide: boolean;
    localeCode: string;
    onOpenPatientRecord: (correction: OpenCorrection) => void;
    onMarkCorrected: (correction: OpenCorrection) => void;
}) {
    const current = correction.flag.current;
    if (!current) return null;
    const flagging = latestFlaggingEvent(correction.flag);
    const toCorrect = current.state === 'OPEN';
    const date = (iso: string) => new Date(iso).toLocaleString(localeCode);

    return (
        <Card variant="outlined" data-testid="correction-row" data-state={current.state} sx={{ p: 2, mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <FlagOutlinedIcon fontSize="small" color={toCorrect ? 'warning' : 'success'} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }} data-testid="correction-visit">
                    <Translate id={`${T}.visit`} />{` ${shortVisitId(correction.visitUuid)}`}
                </Typography>
                <Chip size="small" color={toCorrect ? 'warning' : 'success'} variant="outlined"
                    label={<Translate id={`${T}.state.${current.state}`} />} />
            </Box>
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }} data-testid="correction-flagged-by">
                <Translate id={`${T}.flagged-by`} />
                {` ${personName(flagging?.actor ?? null) ?? '—'}`}
                {flagging && `, ${date(flagging.at)}`}
            </Typography>
            {studyWide && (
                <Typography variant="caption" display="block" data-testid="correction-assignee">
                    <Translate id={`${T}.assignee`} />{` ${personName(current.assignee) ?? '—'}`}
                </Typography>
            )}
            {/* The reasons and the reviewer's note, read-only, as the QC banner shows them. */}
            <Box sx={{ mt: 1 }}>
                <CategoryFeedback
                    readOnly
                    categories={{ status: 'ready', sections: reasonSections }}
                    value={{ categoryIds: current.reasonFieldIds, comment: flagging?.note ?? '' }}
                    onChange={NO_OP}
                />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                <Button size="small" variant="outlined" endIcon={<OpenInNewIcon fontSize="small" />}
                    onClick={() => onOpenPatientRecord(correction)} data-testid="open-patient-record">
                    <Translate id={`${T}.open-patient-record`} />
                </Button>
                {toCorrect && (
                    <Button size="small" variant="contained" startIcon={<CheckIcon fontSize="small" />}
                        onClick={() => onMarkCorrected(correction)} data-testid="mark-corrected">
                        <Translate id={`${T}.mark-corrected`} />
                    </Button>
                )}
            </Box>
        </Card>
    );
}
